/**
 * AI 음성 작성 — 의미 스키마 ↔ 폼 데이터 어댑터
 *
 * 게시판 Write.vue 의 폼 키는 inputA_1, inspectList[3].isChecked 처럼 의미가 드러나지
 * 않는다. LLM 에는 의미 ID(inspect_date, chk_guardrail ...)만 노출하고, 여기서 실제
 * 폼 경로로 되돌린다. 스키마에 없는 의미 ID 는 전부 버린다(환각/주입 방어).
 *
 * ── 스키마 계약 (각 게시판 폴더의 ai-voice-schema.js) ───────────────────────
 * {
 *   boardId: Number,
 *   title:   String,
 *   notes:   [String],                       // 프롬프트에 덧붙일 도메인 힌트(선택)
 *   fields: [{
 *     id, path, label,
 *     type:     'text' | 'textarea' | 'date' | 'number' | 'enum',
 *     enum:     [String],                    // type === 'enum'
 *     required: Boolean,
 *     aliases:  [String],                    // 발화에서 이 필드를 가리키는 표현
 *     hint:     String,
 *     speakerKey: 'name' | 'company' | 'department' | 'section' | 'rank',
 *                                            // 로그인한 작성자 정보로 자동 채울 항목
 *   }],
 *   checklists: [{
 *     id, path, label,
 *     valueEnum: [String],                   // 허용 값(예: 양호/미흡/해당없음)
 *     noteKey:   String,                     // 비고 컬럼 키(선택)
 *     required:  Boolean,
 *     items: [{ id, index, group, label, keywords: [String] }],
 *   }],
 *   ignored:  [String],                      // AI 대상 아님(진행률 분모에서도 제외)
 *   examples: [String],                      // 팝업에 보여줄 예시 발화
 * }
 *
 * ── LLM 이 돌려주는 patch 형태 ─────────────────────────────────────────────
 *   { "inspect_date": "2026-08-07",
 *     "org_name": "대한건설",
 *     "chk_guardrail": "양호",
 *     "chk_foot_switch": { "value": "미흡", "note": "표면 마모, 교체 예정" } }
 */

const EMPTY = (v) => v === undefined || v === null || `${v}`.trim() === "";

/** 스키마의 모든 대상(일반 필드 + 점검표 항목)을 평탄한 목록으로 만든다 */
export function flatten(schema) {
  const entries = [];
  if (!schema) return entries;

  (schema.fields || []).forEach((field) => {
    entries.push({
      kind: "field",
      id: field.id,
      label: field.label,
      group: field.group || "",
      type: field.type || "text",
      enum: field.enum || null,
      required: !!field.required,
      field,
    });
  });

  (schema.checklists || []).forEach((checklist) => {
    (checklist.items || []).forEach((item) => {
      entries.push({
        kind: "check",
        id: item.id,
        label: item.label,
        group: item.group || checklist.label,
        type: "enum",
        enum: checklist.valueEnum || [],
        required: checklist.required !== false,
        checklist,
        item,
      });
    });
  });

  return entries;
}

function findEntry(schema, id) {
  return flatten(schema).find((entry) => entry.id === id);
}

/** 의미 ID 의 현재 값을 폼에서 읽는다 */
export function readValue(form, entry) {
  if (!form || !entry) return "";
  if (entry.kind === "field") {
    return form[entry.field.path];
  }
  const rows = form[entry.checklist.path];
  const row = Array.isArray(rows) ? rows[entry.item.index] : null;
  return row ? row.isChecked : "";
}

function readNote(form, entry) {
  if (!form || !entry || entry.kind !== "check" || !entry.checklist.noteKey) return "";
  const rows = form[entry.checklist.path];
  const row = Array.isArray(rows) ? rows[entry.item.index] : null;
  return row ? row[entry.checklist.noteKey] : "";
}

/**
 * LLM 에 보낼 스키마. 폼 경로(path)는 넘기지 않는다 — LLM 은 의미 ID 만 알면 된다.
 */
export function toLlmSchema(schema) {
  return {
    board_id: schema.boardId,
    title: schema.title,
    notes: schema.notes || [],
    fields: (schema.fields || []).map((field) => ({
      id: field.id,
      label: field.label,
      type: field.type || "text",
      enum: field.enum || undefined,
      required: !!field.required,
      aliases: field.aliases || [],
      hint: field.hint || undefined,
      // 작성자 정보와 연결된 항목임을 서버에 알린다(불필요한 경고 방지).
      speaker_key: field.speakerKey || undefined,
    })),
    checklists: (schema.checklists || []).map((checklist) => ({
      id: checklist.id,
      label: checklist.label,
      value_enum: checklist.valueEnum || [],
      note_enabled: !!checklist.noteKey,
      items: (checklist.items || []).map((item) => ({
        id: item.id,
        group: item.group,
        label: item.label,
        keywords: item.keywords || [],
      })),
    })),
  };
}

/** LLM 에 보낼 현재 폼 상태(의미 ID 기준, 값이 있는 것만) */
export function toFormState(schema, form) {
  const state = {};
  flatten(schema).forEach((entry) => {
    const value = readValue(form, entry);
    if (EMPTY(value)) return;
    const note = readNote(form, entry);
    state[entry.id] = EMPTY(note) ? value : { value, note };
  });
  return state;
}

/** 진행률. 필수 항목 기준으로 계산한다(AI 대상 외 항목은 스키마에 없으므로 자동 제외) */
export function computeProgress(schema, form) {
  const targets = flatten(schema).filter((entry) => entry.required);
  const filled = [];
  const unfilled = [];

  targets.forEach((entry) => {
    if (EMPTY(readValue(form, entry))) unfilled.push(entry);
    else filled.push(entry);
  });

  const total = targets.length;
  return {
    total,
    filledCount: filled.length,
    percent: total === 0 ? 100 : Math.round((filled.length / total) * 100),
    filled,
    unfilled,
  };
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** 값 검증 및 정규화. 허용되지 않는 값은 null 을 돌려 적용에서 제외한다 */
function normalize(entry, raw) {
  if (EMPTY(raw)) return null;
  const value = `${raw}`.trim();

  if (entry.type === "date") {
    return DATE_RE.test(value) ? value : null;
  }
  if (entry.type === "number") {
    return Number.isNaN(Number(value)) ? null : value;
  }
  if (entry.enum && entry.enum.length > 0) {
    return entry.enum.includes(value) ? value : null;
  }
  return value;
}

/**
 * 작성자 정보(로그인 사용자)로 채울 수 있는 항목을 patch 형태로 돌려준다.
 *
 * 현장에서는 "오늘 내가 점검했고..." 처럼 소속·이름을 아예 말하지 않는 경우가 많다.
 * 스키마에 speakerKey 가 선언된 항목이 비어 있으면 작성자 정보로 채워 제안한다.
 * (AI 가 이미 채운 항목은 그 값을 우선한다 — 발화가 항상 이긴다)
 *
 * @returns {Object} { 의미ID: 값 }
 */
export function speakerPatch(schema, form, speaker, skipIds) {
  const patch = {};
  if (!speaker) return patch;

  const skip = skipIds || [];
  (schema.fields || []).forEach((field) => {
    if (!field.speakerKey || skip.indexOf(field.id) !== -1) return;

    const value = speaker[field.speakerKey];
    if (EMPTY(value)) return;
    // 이미 폼에 값이 있으면 건드리지 않는다.
    if (!EMPTY(form ? form[field.path] : "")) return;

    patch[field.id] = `${value}`.trim();
  });

  return patch;
}

/**
 * LLM patch 를 사람이 확인할 수 있는 변경 목록으로 바꾼다.
 * 실제 적용은 사용자가 고른 항목만 applyChanges 로 반영한다.
 *
 * @returns {{changes: Array, rejected: Array}}
 *   changes  [{ id, label, group, before, after, note, overwrite, selected }]
 *   rejected [{ id, reason, raw }]
 */
export function describePatch(schema, form, patch) {
  const changes = [];
  const rejected = [];

  Object.keys(patch || {}).forEach((id) => {
    const entry = findEntry(schema, id);
    if (!entry) {
      rejected.push({ id, reason: "스키마에 없는 항목", raw: patch[id] });
      return;
    }

    const raw = patch[id];
    const isObject = raw && typeof raw === "object" && !Array.isArray(raw);
    const after = normalize(entry, isObject ? raw.value : raw);
    const note = isObject && !EMPTY(raw.note) ? `${raw.note}`.trim() : "";

    if (after === null && EMPTY(note)) {
      rejected.push({ id, reason: "허용되지 않는 값", raw });
      return;
    }

    const before = readValue(form, entry);
    const beforeNote = readNote(form, entry);
    if (`${before || ""}` === `${after || ""}` && `${beforeNote || ""}` === note) return;

    changes.push({
      id,
      label: entry.label,
      group: entry.group,
      before: before || "",
      after: after === null ? before || "" : after,
      note,
      beforeNote: beforeNote || "",
      overwrite: !EMPTY(before),
      selected: true,
    });
  });

  return { changes, rejected };
}

/**
 * 선택된 변경만 폼에 반영한다. 원본을 직접 바꾸지 않고 복사본을 돌려준다.
 */
export function applyChanges(schema, form, changes) {
  const next = JSON.parse(JSON.stringify(form || {}));

  (changes || []).forEach((change) => {
    if (change.selected === false) return;
    const entry = findEntry(schema, change.id);
    if (!entry) return;

    if (entry.kind === "field") {
      next[entry.field.path] = change.after;
      return;
    }

    const rows = next[entry.checklist.path];
    if (!Array.isArray(rows) || !rows[entry.item.index]) return;
    if (!EMPTY(change.after)) rows[entry.item.index].isChecked = change.after;
    if (entry.checklist.noteKey && !EMPTY(change.note)) {
      rows[entry.item.index][entry.checklist.noteKey] = change.note;
    }
  });

  return next;
}
